import { WarrantyType } from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { privateAxios } from "../axios";
import { pricingFilesQueryKeys } from "../query-keys/pricing-files.query-keys";

export interface CreatePricingFileDto {
	bucket: "private" | "public";
	key: string;
	name: string;
	mimeType: string;
	size?: number;
	warrantyType: WarrantyType;
	binderAmendmentId: number;
}

export const createOrUpdatePricingFile = async (dto: CreatePricingFileDto) => {
	const { bucket, key, name, mimeType, size, warrantyType, binderAmendmentId } =
		dto;
	const { data } = await privateAxios.put("/pricing-files", {
		bucket,
		key,
		name,
		mimeType,
		size,
		warrantyType,
		binderAmendmentId,
	});
	return data;
};

export const useCreateOrUpdatePricingFile = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: createOrUpdatePricingFile,
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: pricingFilesQueryKeys.urlByWarrantyTypeAndBinderAmendmentId(
					variables.warrantyType,
					variables.binderAmendmentId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: pricingFilesQueryKeys.all,
			});
			toast.success("Fichier de tarification importé avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'import du fichier", {
				description: errorMessage,
			});
		},
	});
};

export const deletePricingFile = async (id: number) => {
	const { data } = await privateAxios.delete(`/pricing-files/${id}`);
	return data;
};

export const useDeletePricingFile = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deletePricingFile,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: pricingFilesQueryKeys.all,
			});
			toast.success("Fichier de tarification supprimé avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la suppression du fichier", {
				description: errorMessage,
			});
		},
	});
};

export const deletePricingFileByWarrantyTypeAndBinderAmendmentId = async ({
	warrantyType,
	binderAmendmentId,
}: {
	warrantyType: WarrantyType;
	binderAmendmentId: number;
}) => {
	const { data } = await privateAxios.delete("/pricing-files", {
		params: {
			warrantyType,
			binderAmendmentId,
		},
	});
	return data;
};

export const useDeletePricingFileByWarrantyTypeAndBinderAmendmentId = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deletePricingFileByWarrantyTypeAndBinderAmendmentId,
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: pricingFilesQueryKeys.urlByWarrantyTypeAndBinderAmendmentId(
					variables.warrantyType,
					variables.binderAmendmentId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: pricingFilesQueryKeys.all,
			});
			toast.success("Fichier de tarification supprimé avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la suppression du fichier", {
				description: errorMessage,
			});
		},
	});
};
